/**
 * React Query hooks for the Twin module HTTP surface.
 *
 * Mirrors backend/twin/cascade_router.py + backend/twin/chain_router.py
 * + backend/twin/capability_map_router.py + backend/twin/findings_router.py.
 * Module-boundary rule: this file may import from `twin/*` and
 * `shared/*` only -- no foundation/, no framework/. The same convention
 * as framework/api/hooks.ts.
 *
 * Hosts:
 *   useCascade        -- GET /v1/cascade (15s polling, P2.6)
 *   useFindings       -- GET /v1/findings filtered to one entity (P2.7)
 *
 * Chain READ hooks (resilience/blast/process/obligation) land with the
 * entity detail pages in P2.7.e.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import {
  cascadeKeys,
  chainDiffKeys,
  chainTrendKeys,
  channelHeatmapKeys,
  entityKeys,
  findingsKeys,
  journeyKeys,
  landscapeKeys,
  obligationKeys,
  obligationTraceabilityKeys,
} from "shared/api/queryClient";
import type {
  DependencyStack,
  CascadeSummary,
  ConfirmedEquivalence,
  ChainDiff,
  ChainName,
  ChannelHeatmap,
  CountTrend,
  EntityBasic,
  EntityKind,
  Finding,
  IBSStory,
  JourneyDetail,
  JourneySummary,
  LandscapeConfidence,
  LandscapeOverlay,
  LandscapeView,
  ObligationEquivalents,
  ObligationSnapshot,
  ResilienceSnapshot,
  ObligationTraceability,
  ResilienceTrend,
  Severity,
  TileDetail,
} from "shared/api/types";

/**
 * 15 seconds. Matches the v2-platform-plan polling cadence -- fresh
 * enough that an operator watching for a degraded service sees the
 * impact within one screen-glance, slow enough to keep the load on
 * the cascade aggregator linear (each tick is one DB query plus the
 * per-impact chain fetches; see docs/p2.6-cascade-plan.md backlog
 * #424 for the batched-reader follow-up).
 */
export const CASCADE_POLL_MS = 15_000;

export interface UseCascadeOptions {
  /** ISO-8601 datetime string (tz-aware). When set, replays chain state at this instant. */
  as_of?: string | undefined;
  /** Severity allow-list. Defaults to ['critical', 'high'] server-side when omitted. */
  severities?: readonly Severity[] | undefined;
  /** Findings cap. Defaults to 50 server-side; max 200. */
  limit?: number | undefined;
}

function buildCascadePath(opts: UseCascadeOptions): string {
  const params = new URLSearchParams();
  if (opts.as_of) {
    params.set("as_of", opts.as_of);
  }
  if (opts.severities && opts.severities.length > 0) {
    params.set("severities", opts.severities.join(","));
  }
  if (typeof opts.limit === "number") {
    params.set("limit", String(opts.limit));
  }
  const query = params.toString();
  return query ? `/v1/cascade?${query}` : "/v1/cascade";
}

/**
 * Subscribe to the live cascade view.
 *
 * Polls every 15s (CASCADE_POLL_MS) via React Query's refetchInterval.
 * staleTime: 0 so each interval really refetches rather than serving
 * the cached row. The query key embeds (as_of, severities, limit), so
 * changing any of those reuses the cache where appropriate or refetches
 * cleanly when shifted.
 *
 * Auth: caller must have a valid bearer token in sessionStorage (or
 * via setAuthTokenGetter). Endpoint returns 401 for anonymous; the
 * 401 surfaces as ApiError on this hook's `error`.
 */
export function useCascade(
  opts: UseCascadeOptions = {},
): UseQueryResult<CascadeSummary, ApiError> {
  return useQuery({
    queryKey: cascadeKeys.view({
      as_of: opts.as_of,
      severities: opts.severities,
      limit: opts.limit,
    }),
    queryFn: () => api.get<CascadeSummary>(buildCascadePath(opts)),
    refetchInterval: CASCADE_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}

// ── Capability map (P2.7) ───────────────────────────────────────────

// ── Per-entity findings (P2.7) ──────────────────────────────────────

export interface UseFindingsOptions {
  entity_kind: string;
  entity_id: string;
  status?: string | undefined;
  severities?: readonly Severity[] | undefined;
  limit?: number | undefined;
  /** Disable the query (e.g. when entity_id isn't resolved yet --
   *  CellDrawer mounts with cell=null and fires useFindings with
   *  entity_id="" which the backend 422s on). Defaults to "enabled
   *  when entity_id is a non-empty string". Callers can override
   *  to force-disable. */
  enabled?: boolean | undefined;
}

function buildFindingsPath(opts: UseFindingsOptions): string {
  const params = new URLSearchParams();
  params.set("entity_kind", opts.entity_kind);
  params.set("entity_id", opts.entity_id);
  if (opts.status) {
    params.set("status", opts.status);
  }
  if (opts.severities && opts.severities.length > 0) {
    params.set("severities", opts.severities.join(","));
  }
  if (typeof opts.limit === "number") {
    params.set("limit", String(opts.limit));
  }
  return `/v1/findings?${params.toString()}`;
}

/**
 * List findings on one entity.
 *
 * Powers the per-entity panel on entity detail pages + the
 * CellDrawer's "impacted findings" subsection. (entity_kind,
 * entity_id) is required; omit `enabled` and it always fires.
 */
export function useFindings(
  opts: UseFindingsOptions,
): UseQueryResult<Finding[], ApiError> {
  const enabled = opts.enabled ?? opts.entity_id !== "";
  return useQuery({
    queryKey: findingsKeys.forEntity({
      entity_kind: opts.entity_kind,
      entity_id: opts.entity_id,
      status: opts.status,
      severities: opts.severities,
      limit: opts.limit,
    }),
    queryFn: () => api.get<Finding[]>(buildFindingsPath(opts)),
    enabled,
    staleTime: 30_000,
  });
}

// ── Entity basic detail (P2.7) ──────────────────────────────────────

/**
 * Look up one entity's identity (id, kind, code, name, description).
 *
 * Used by the 5 entity detail pages to render the header above the
 * chip strip + findings panel. The cascade + capability-map
 * aggregators already surface operational state; this hook fills
 * in the WHO of the entity.
 *
 * Disabled when `id` is missing (the route param hasn't resolved
 * yet); React Query skips the fetch + reports isLoading=false.
 */
export function useEntityDetail(
  kind: EntityKind,
  id: string | undefined,
): UseQueryResult<EntityBasic, ApiError> {
  const enabled = !!id;
  return useQuery({
    queryKey: id ? entityKeys.detail(kind, id) : [...entityKeys.all, kind, "_disabled"],
    queryFn: () => api.get<EntityBasic>(`/v1/entities/${kind}/${id}`),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * P102: the per-entity OBLIGATION snapshot -- which regulatory clauses
 * apply to this entity. Powers the "my obligations" panel on the entity
 * detail pages.
 *
 * A 404 means no snapshot has been sealed yet (lazy-genesis entity) --
 * an expected empty state, NOT an error -- so we don't retry it. The
 * panel renders the empty state off `error.status === 404`.
 */
export function useObligationSnapshot(
  kind: EntityKind,
  id: string | undefined,
  // Round 9 E9 -- the obligation deriver seals ibs_service / service /
  // capability and nothing else, so a vendor page asked for a snapshot
  // that is never coming and rendered the 404 as an empty panel. The
  // caller decides whether the question applies; see
  // twin/entity/derivedChains.ts.
  applies = true,
): UseQueryResult<ObligationSnapshot, ApiError> {
  const enabled = !!id && applies;
  return useQuery({
    queryKey: id
      ? obligationKeys.detail(kind, id)
      : [...obligationKeys.all, kind, "_disabled"],
    queryFn: () =>
      api.get<ObligationSnapshot>(`/v1/twin/chains/obligation/${kind}/${id}`),
    enabled,
    staleTime: 60_000,
    retry: (count, err) => err.status !== 404 && count < 2,
  });
}

/**
 * P272.4: the per-entity RESILIENCE-STATE snapshot -- the SAME chain the
 * cascade reads (P98/P99), so the entity detail page's resilience chip
 * matches the cascade instead of showing a hardcoded "n/a". A 404 (never
 * snapshotted) is an expected empty state, not an error -- the chip falls
 * back to null.
 */
export function useResilienceSnapshot(
  kind: EntityKind,
  id: string | undefined,
  // Round 9 E9 -- same as useObligationSnapshot: a vendor has no
  // resilience state of its own in this model.
  applies = true,
): UseQueryResult<ResilienceSnapshot, ApiError> {
  return useQuery({
    queryKey: id
      ? (["twin", "resilience-state", kind, id] as const)
      : (["twin", "resilience-state", kind, "_disabled"] as const),
    queryFn: () =>
      api.get<ResilienceSnapshot>(`/v1/twin/chains/resilience-state/${kind}/${id}`),
    enabled: !!id && applies,
    staleTime: 60_000,
    retry: (count, err) => err.status !== 404 && count < 2,
  });
}

/**
 * P275: the IBS entity-lifecycle hub -- one IBS's whole story (admission /
 * tolerance / MVB / resilience / obligations / scenarios) for the detail
 * page's story-hub chip row.
 */
export function useIBSDependencyLayers(
  id: string | undefined,
  /** A2: lift the per-layer item cap once a reader asks for the rest.
   *  Part of the query key, so asking refetches rather than serving
   *  the twelve already cached. */
  full = false,
): UseQueryResult<DependencyStack, ApiError> {
  return useQuery({
    queryKey: id
      ? (["twin", "ibs-layers", id, full] as const)
      : (["twin", "ibs-layers", "_disabled"] as const),
    queryFn: () =>
      api.get<DependencyStack>(
        `/v1/twin/ibses/${id}/dependency-layers${full ? "?full=true" : ""}`,
      ),
    enabled: !!id,
    staleTime: 60_000,
    retry: (count, err) => err.status !== 404 && count < 2,
  });
}

export function useIBSStory(
  id: string | undefined,
): UseQueryResult<IBSStory, ApiError> {
  return useQuery({
    queryKey: id
      ? (["twin", "ibs-story", id] as const)
      : (["twin", "ibs-story", "_disabled"] as const),
    queryFn: () => api.get<IBSStory>(`/v1/twin/ibses/${id}/story`),
    enabled: !!id,
    staleTime: 60_000,
    retry: (count, err) => err.status !== 404 && count < 2,
  });
}

/**
 * P105: the controls implementing one obligation + their assessment
 * status (the P104 traceability endpoint). Lazy -- only fetched when a
 * clause is expanded. A 403 (no framework module) is an expected
 * degraded state, not a retry-worthy error.
 */
export function useObligationTraceability(
  obligationId: string,
  enabled: boolean,
): UseQueryResult<ObligationTraceability, ApiError> {
  return useQuery({
    queryKey: obligationTraceabilityKeys.detail(obligationId),
    queryFn: () =>
      api.get<ObligationTraceability>(
        `/v1/framework/obligations/${obligationId}/traceability`,
      ),
    enabled: enabled && obligationId !== "",
    staleTime: 60_000,
    retry: (count, err) => err.status !== 404 && err.status !== 403 && count < 2,
  });
}

/**
 * P140: Engine finds obligations in OTHER frameworks that state the same
 * requirement as this one (cross-framework dedup). On-demand, read-only;
 * triggered by a button so the Engine call is explicit. 503 when the
 * Engine isn't configured surfaces as a mutation error.
 */
export function useFindObligationEquivalents(
  obligationId: string,
): UseMutationResult<ObligationEquivalents, ApiError, void> {
  return useMutation({
    mutationFn: () =>
      api.post<ObligationEquivalents>(
        `/v1/framework/obligations/${obligationId}/find-equivalents`,
      ),
  });
}

const equivalencesKey = (id: string) =>
  ["framework", "obligation", id, "equivalences"] as const;

/** P231: the persisted (confirmed) equivalences for an obligation -- the
 *  always-on view, no Engine call. Lazy: enabled when the panel is open. */
export function useObligationEquivalences(
  obligationId: string,
  enabled: boolean,
): UseQueryResult<ConfirmedEquivalence[], ApiError> {
  return useQuery({
    queryKey: equivalencesKey(obligationId),
    queryFn: () =>
      api.get<ConfirmedEquivalence[]>(
        `/v1/framework/obligations/${obligationId}/equivalences`,
      ),
    enabled,
  });
}

/** P231: confirm a cross-framework equivalence (persist the pair). */
export function useConfirmEquivalence(
  obligationId: string,
): UseMutationResult<ConfirmedEquivalence, ApiError, ConfirmEquivalenceVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: ConfirmEquivalenceVars) =>
      api.post<ConfirmedEquivalence>(
        `/v1/framework/obligations/${obligationId}/equivalences`,
        vars,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: equivalencesKey(obligationId) });
    },
  });
}

export interface ConfirmEquivalenceVars {
  target_obligation_id: string;
  confidence?: number;
  rationale?: string;
  source?: "engine" | "manual";
}

/** P231: un-confirm an equivalence. */
export function useDeleteEquivalence(
  obligationId: string,
): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) =>
      api.delete<void>(
        `/v1/framework/obligations/${obligationId}/equivalences/${linkId}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: equivalencesKey(obligationId) });
    },
  });
}

// ── Chain DIFF (P2.8) ───────────────────────────────────────────────

export interface UseChainDiffOptions {
  chain: ChainName;
  entity_kind: EntityKind;
  entity_id: string;
  /** ISO 8601 datetime with offset. */
  from: string | null;
  to: string | null;
}

/**
 * Subscribe to a chain DIFF.
 *
 * Disabled unless all of (chain, entity_kind, entity_id, from, to)
 * are set -- the ChainDiffPanel keeps the panel collapsed (or shows
 * a "pick dates" prompt) until the user commits both anchors. Avoids
 * firing /diff with empty params -> 422.
 */
export function useChainDiff(
  opts: UseChainDiffOptions,
): UseQueryResult<ChainDiff, ApiError> {
  // Narrow the nullable from/to upfront so the queryFn doesn't need
  // non-null assertions (which lint forbids). When either anchor is
  // null we just keep the hook disabled and the queryFn is never
  // invoked.
  const from = opts.from ?? "";
  const to = opts.to ?? "";
  const enabled = from !== "" && to !== "" && opts.entity_id !== "";
  const queryKey = enabled
    ? chainDiffKeys.query({
        chain: opts.chain,
        entity_kind: opts.entity_kind,
        entity_id: opts.entity_id,
        from,
        to,
      })
    : [...chainDiffKeys.all, "_disabled"];
  return useQuery({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("from", from);
      params.set("to", to);
      return api.get<ChainDiff>(
        `/v1/twin/chains/${opts.chain}/${opts.entity_kind}/${opts.entity_id}/diff?${params.toString()}`,
      );
    },
    enabled,
    staleTime: 60_000,
  });
}

// ── Chain TREND (P2.9) ──────────────────────────────────────────────

export interface UseResilienceTrendOptions {
  window?: number | undefined;
  entity_kind?: string | undefined;
}

/**
 * Subscribe to the resilience-state TREND.
 *
 * Powers the ResilienceTrendCard on Home. No polling -- the trend
 * is slow-moving operational data. staleTime: 60s keeps the card
 * snappy on Home re-mount without hammering the aggregator.
 */
export function useResilienceTrend(opts: UseResilienceTrendOptions = {}) {
  const window = opts.window ?? 30;
  return useQuery<ResilienceTrend, ApiError>({
    queryKey: chainTrendKeys.query({
      chain: "resilience-state",
      window,
      entity_kind: opts.entity_kind,
    }),
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("window", String(window));
      if (opts.entity_kind) {
        params.set("entity_kind", opts.entity_kind);
      }
      return api.get<ResilienceTrend>(
        `/v1/twin/chains/resilience-state/trend?${params.toString()}`,
      );
    },
    staleTime: 60_000,
  });
}

/** Same shape for blast / process / obligation. No frontend
 *  consumer in v0.2.0; ships now so future cards skip the hook
 *  authoring step. */
export function useCountTrend(
  chain: "blast-radius" | "process" | "obligation",
  opts: UseResilienceTrendOptions = {},
) {
  const window = opts.window ?? 30;
  return useQuery<CountTrend, ApiError>({
    queryKey: chainTrendKeys.query({
      chain,
      window,
      entity_kind: opts.entity_kind,
    }),
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("window", String(window));
      if (opts.entity_kind) {
        params.set("entity_kind", opts.entity_kind);
      }
      return api.get<CountTrend>(`/v1/twin/chains/${chain}/trend?${params.toString()}`);
    },
    staleTime: 60_000,
  });
}

// ── Landscape view (P6) ─────────────────────────────────────────────

export interface UseLandscapeOptions {
  /** ISO-8601 datetime (tz-aware). When set, replays chain state at
   *  this instant. AsOfPicker drives this. */
  as_of?: string | undefined;
  /** Filter mappings to ones with confidence >= this level. */
  confidence_min?: LandscapeConfidence | undefined;
  /** Overlay mode: confidence / mvb / cascade / both. */
  overlay?: LandscapeOverlay | undefined;
}

function buildLandscapePath(catalogCode: string, opts: UseLandscapeOptions): string {
  const params = new URLSearchParams();
  if (opts.as_of) {
    params.set("as_of", opts.as_of);
  }
  if (opts.confidence_min) {
    params.set("confidence_min", opts.confidence_min);
  }
  if (opts.overlay) {
    params.set("overlay", opts.overlay);
  }
  const query = params.toString();
  const base = `/v1/landscape/${encodeURIComponent(catalogCode)}`;
  return query ? `${base}?${query}` : base;
}

/**
 * Subscribe to a landscape catalogue's rich view.
 *
 * Catalogue + ontology + per-tile rollups + overlay state. No polling
 * by default -- the landscape changes slowly relative to cascade. The
 * caller controls staleness via the AsOfPicker; bumping ?as_of= shifts
 * the query key and React Query refetches.
 */
export function useLandscape(
  catalogCode: string,
  opts: UseLandscapeOptions = {},
): UseQueryResult<LandscapeView, ApiError> {
  return useQuery({
    queryKey: landscapeKeys.view({
      catalogCode,
      as_of: opts.as_of,
      confidence_min: opts.confidence_min,
      overlay: opts.overlay,
    }),
    queryFn: () => api.get<LandscapeView>(buildLandscapePath(catalogCode, opts)),
    staleTime: 30_000,
  });
}

export interface UseTileDetailOptions {
  as_of?: string | undefined;
}

/**
 * Per-tile drawer payload -- mapped services + their resilience state
 * + open-finding counts + MVB-linkage flag. Fetched lazily when the
 * drawer opens (`enabled` flag).
 */
export function useTileDetail(
  tileId: string | null,
  opts: UseTileDetailOptions = {},
): UseQueryResult<TileDetail, ApiError> {
  return useQuery({
    queryKey: landscapeKeys.tile({
      tileId: tileId ?? "__none__",
      as_of: opts.as_of,
    }),
    queryFn: () => {
      const params = new URLSearchParams();
      if (opts.as_of) {
        params.set("as_of", opts.as_of);
      }
      const query = params.toString();
      const base = `/v1/landscape/tiles/${encodeURIComponent(tileId ?? "")}`;
      return api.get<TileDetail>(query ? `${base}?${query}` : base);
    },
    enabled: tileId !== null,
    staleTime: 30_000,
  });
}

// ── Channel heatmap (P8.3) ──────────────────────────────────────────

export interface UseChannelHeatmapOptions {
  /** ISO-8601 datetime (tz-aware). When set, replays IBS
   *  resilience pillar at this instant. AsOfPicker drives this. */
  as_of?: string | undefined;
}

/**
 * Subscribe to the channel heatmap cross-tab.
 *
 * Returns the {channels, ibses, cells} matrix + per-IBS resilience
 * pillar at as_of. staleTime 30s -- the matrix changes slowly
 * relative to the cascade; as_of bumps invalidate the cache via
 * the query key.
 */
export function useChannelHeatmap(
  opts: UseChannelHeatmapOptions = {},
): UseQueryResult<ChannelHeatmap, ApiError> {
  const path = opts.as_of
    ? `/v1/heatmap/channels?as_of=${encodeURIComponent(opts.as_of)}`
    : "/v1/heatmap/channels";
  return useQuery({
    queryKey: channelHeatmapKeys.view({ as_of: opts.as_of }),
    queryFn: () => api.get<ChannelHeatmap>(path),
    staleTime: 30_000,
  });
}

// ── Customer journeys (P16.2 / #485) ────────────────────────────────

export interface UseJourneysOptions {
  ibs_service_id?: string | undefined;
}

/**
 * List customer journeys, optionally filtered to one IBS.
 *
 * Used by the JourneysView rail (no filter) + the IBS detail page's
 * "journeys for this IBS" section once we wire that.
 */
export function useJourneys(
  opts: UseJourneysOptions = {},
): UseQueryResult<JourneySummary[], ApiError> {
  const path = opts.ibs_service_id
    ? `/v1/journeys?ibs_service_id=${encodeURIComponent(opts.ibs_service_id)}`
    : "/v1/journeys";
  return useQuery({
    queryKey: journeyKeys.list({ ibs_service_id: opts.ibs_service_id }),
    queryFn: () => api.get<JourneySummary[]>(path),
    staleTime: 60_000,
  });
}

/**
 * One journey with inline ordered steps. Lazy: only fetches when an
 * id is supplied (the rail unselects -> id=null and we skip the
 * request).
 */
export function useJourney(
  id: string | undefined,
): UseQueryResult<JourneyDetail, ApiError> {
  return useQuery({
    queryKey: id ? journeyKeys.detail(id) : [...journeyKeys.all, "detail", "_disabled"],
    queryFn: () => api.get<JourneyDetail>(`/v1/journeys/${id}`),
    enabled: !!id,
    staleTime: 60_000,
  });
}
